import classNames from 'classnames';
import PropTypes from 'prop-types';
import { mapProps } from '../../hoc/recompose';
import Arrow from '../Arrow';
import { Option as PlainOption, Select as PlainSelect } from './plain';
import styles from './style.m.less';

const createStandardSelected =
    (mapper, classes) =>
    ({ selected, isOpen, hasError, disabled }) => {
        const controlCn = classNames(
            styles.control,
            {
                [styles.isOpen]: isOpen,
                [styles.hasError]: hasError,
                [styles.disabled]: disabled,
            },
            classes
        );
        return (
            <div className={controlCn}>
                <span>{mapper(selected)}</span>
                <div className={styles.arrow}>
                    <Arrow dir="DOWN" />
                </div>
            </div>
        );
    };

const createStandardPlaceholder = () => createStandardSelected(() => '--');

const createStandardOption = (mapper, getKey) => value => {
    return (
        <PlainOption key={getKey(value)} value={value} className={styles.option}>
            {mapper(value)}
        </PlainOption>
    );
};

const createOptionsFromProps = props => {
    return props.options
        ? Array.prototype.concat.call(
              props.children || [],
              props.options.map(createStandardOption(props.optionToText, props.optionToKey || props.optionToText))
          )
        : props.children;
};

const createPlaceholderFromProps = props => {
    if (props.placeholder === undefined) {
        return createStandardPlaceholder();
    }
    if (typeof props.placeholder === 'string') {
        return createStandardSelected(() => props.placeholder);
    }
    return props.placeholder;
};

const Select = mapProps(({ kind = 'DEFAULT', ...props }) => {
    return {
        ...props,
        kind,
        menuClassName: classNames(styles.menu, props.menuClassName),
        selectedClassName: classNames(styles.selected, props.selectedClassName),
        highlightedClassName: styles.highlighted,
        buttonClassName: classNames(
            {
                [styles.button]: kind !== 'PLAIN',
                [styles.buttonWithError]: props.hasError,
                [styles.buttonDefault]: kind === 'DEFAULT',
                [styles.buttonTitle]: kind === 'TITLE',
                [styles.buttonSmall]: props.isSmall,
                [styles.buttonMaxHeight]: props.buttonMaxHeight,
            },
            props.buttonClassName
        ),
        placeholder: createPlaceholderFromProps(props),
        selectComponent: props.selectComponent || createStandardSelected(props.optionToText, props.controlClassName),
        // Below splice because we'd like to override the children spliced in from props.
        children: createOptionsFromProps(props),
    };
})(PlainSelect);

Select.propTypes = {
    onSelect: PropTypes.func,
    options: PropTypes.array,
    optionToKey: PropTypes.func,
    optionToText: PropTypes.func,
    placeholder: PropTypes.any,
    selected: PropTypes.any,
    buttonClassName: PropTypes.string,
    buttonMaxHeight: PropTypes.bool,
    isSmall: PropTypes.bool,
    kind: PropTypes.oneOf(['DEFAULT', 'TITLE', 'PLAIN']),
    dataIntercomTarget: PropTypes.string,
    fullWidthMenu: PropTypes.bool,
};

/** @type { React.FC<any> } */
const Option = ({ className, ...props }) => <PlainOption className={classNames(styles.option, className)} {...props} />;

export const OptionHeader = ({ name }) => <div className={styles.optionHeader}>{name}</div>;
export const OptionDivider = () => <hr className={styles.optionDivider} />;

export { Arrow, createStandardOption, createStandardPlaceholder, createStandardSelected, Option, PlainOption, Select };
